/**
 * Public run-only view for embedding or sharing. No edit/nav chrome.
 * Route: /p/:id
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { DashboardProvider } from '../contexts/DashboardContext';
import { getDashboardConfigPublic, getDashboardConfig } from '../api/dashboardConfig.api';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import AppHeader from '../components/Dashboard/AppHeader';
import AppThemeProvider, { getColorsFromConfig } from '../components/Dashboard/AppThemeProvider';
import GlobalFiltersBar from '../components/Dashboard/GlobalFiltersBar';
import DashboardChatBar from '../components/Dashboard/DashboardChatBar';

export default function DashboardPublishPage({ darkMode }) {
  const { id } = useParams();
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) {
        setLoadError('App ID required');
        return;
      }
      let { data, error: err } = await getDashboardConfigPublic(id);
      if (!mounted) return;
      if (err) {
        const authRes = await getDashboardConfig(id);
        if (!mounted) return;
        if (!authRes.error && authRes.data) {
          data = authRes.data;
          err = null;
        }
      }
      if (err) {
        setLoadError(err);
        return;
      }
      setConfig(data);
    })();
    return () => { mounted = false; };
  }, [id]);

  if (loadError) {
    return (
      <Box sx={{ p: 4, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  const t = config?.theme || {};
  const headerStyle = t.headerStyle || 'flat';
  const isGradient = headerStyle === 'gradient';
  const colors = getColorsFromConfig(t);
  const hexToRgb = (hex) => {
    const h = hex.replace(/^#/, '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)].join(',');
  };
  const gradientBg = isGradient ? `linear-gradient(180deg, rgba(${hexToRgb(colors.primary)}, 0.06) 0%, transparent 18%)` : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100vh',
        minHeight: 0,
        overflow: 'hidden',
        ...(gradientBg && { background: gradientBg }),
      }}
    >
      <AppThemeProvider key={config._id || config.id} config={config} darkMode={darkMode}>
        <DashboardProvider config={config}>
          <AppHeader config={config} />
          {config.chatPosition === 'header' && <DashboardChatBar darkMode={darkMode} />}
          <GlobalFiltersBar />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <DashboardLayout config={config} darkMode={darkMode} />
          </Box>
        </DashboardProvider>
      </AppThemeProvider>
    </Box>
  );
}
